import { Injectable, inject, signal } from '@angular/core';
import { UserProfileService } from './user-profile.service';

/**
 * Single source of truth for the logged-in user's profile picture.
 *
 * Fetches once per session (deduped) and exposes the value app-wide as a
 * readonly signal, so header / profile / dashboard never issue duplicate
 * requests and stay in sync after an upload (`refresh()`) or logout
 * (`clear()`).
 */
@Injectable({ providedIn: 'root' })
export class UserProfileStoreService {
  private userProfileService = inject(UserProfileService);

  private readonly _profilePic = signal<string | null>(null);
  /** Current profile photo URL, or null when absent / logged out. */
  readonly profilePic = this._profilePic.asReadonly();

  private loaded = false;

  /** Fetch the photo once; pass `force` to re-fetch (e.g. after upload). */
  loadProfilePic(force = false): void {
    if (this.loaded && !force) {
      return;
    }
    const userId = this.userIdFromToken();
    if (userId == null) {
      return;
    }
    this.loaded = true;
    this.userProfileService.getUserProfilePic(userId).subscribe({
      next: (res: any) => this._profilePic.set(res?.body?.data?.photo || null),
      // Allow the next trigger to retry instead of caching the failure.
      error: () => (this.loaded = false),
    });
  }

  /** Re-fetch after the user uploads or resets their photo. */
  refresh(): void {
    this.loadProfilePic(true);
  }

  /** Reset on logout so the next login can't show a stale avatar. */
  clear(): void {
    this._profilePic.set(null);
    this.loaded = false;
  }

  private userIdFromToken(): string | number | null {
    if (
      typeof window === 'undefined' ||
      typeof window.localStorage === 'undefined'
    ) {
      return null;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.user_id ?? null;
    } catch {
      return null; // malformed JWT
    }
  }
}
